import { computed, Injectable, signal } from '@angular/core';
import { ChatMessage } from '../models/chat-message';
import { environment } from '../../../environments/environment';

const CHAT_THREADS_STORAGE_KEY = 'serg-chat-threads-v1';

export interface ChatThread {
  id: string;
  title: string;
  messages: ChatMessage[];
  /** Follow-up topics for the current completed turn; omitted until they are generated. */
  relatedTopics?: string[];
  /** Identifies the conversation state the current title was generated for, so it is not regenerated on reopen. */
  titledKey?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Settings {
  /** OpenAI-compatible endpoint for the primary conversation model. */
  mainApiUrl: string;
  model?: string;
  temperature?: number;
  reasoningEffort?: 'low' | 'medium' | 'high';
  /** When enabled, full prompts and response metadata are written to the browser console. */
  logLlmRequests?: boolean;
  /** Whether to generate and show follow-up topics after an assistant response. */
  relatedTopicGenerationEnabled?: boolean;
  /** Optional lightweight model used only to prepare retrieval and routing decisions. */
  helperModel?: HelperModelSettings;
}

export interface HelperModelSettings {
  enabled: boolean;
  /** When empty, helper work is sent to the primary API. */
  apiUrl?: string;
  modelId?: string;
}

/** Implemented by the optional authenticated Supabase synchronization layer. */
export interface ChatThreadSync {
  upsert(thread: ChatThread): void;
  delete(threadId: string): void;
  clear(): void;
}

export const DEFAULT_SETTINGS: Required<Settings> = {
  mainApiUrl: environment.rootUrl,
  model: environment.llmModel,
  temperature: 0.7,
  reasoningEffort: 'medium',
  logLlmRequests: false,
  relatedTopicGenerationEnabled: true,
  helperModel: { enabled: false },
};

@Injectable({
  providedIn: 'root'
})
export class ChatPersistenceService {
  private readonly persistedThreads = signal<ChatThread[]>(this.readThreads());
  private threadSync?: ChatThreadSync;
  readonly activeThreadId = signal<string | null>(null);

  readonly threads = this.persistedThreads.asReadonly();

  readonly activeThread = computed(() => {
    const activeId = this.activeThreadId();
    return activeId ? this.persistedThreads().find((thread) => thread.id === activeId) : undefined;
  });
  readonly activeMessages = computed(() => this.activeThread()?.messages ?? []);

  /** Registers remote sync after authentication has completed. */
  setThreadSync(sync: ChatThreadSync | undefined): void {
    this.threadSync = sync;
  }

  /** Replaces the local cache from Supabase without writing it straight back. */
  hydrateThreads(threads: ChatThread[]): void {
    this.persistedThreads.set(threads.map((thread) => this.withMessageIds(thread)).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
    this.persist();
  }

  /** Migrates any cached threads to the authenticated account after initial load. */
  syncCachedThreads(): void {
    for (const thread of this.persistedThreads()) this.threadSync?.upsert(thread);
  }

  /** Finds a requested thread or returns the most recently active conversation. */
  openThread(threadId?: string | null): ChatThread {
    if (threadId) {
      const requested = this.findThread(threadId) ?? this.createThread();
      this.activeThreadId.set(requested.id);
      return requested;
    }

    const active = this.activeThread();
    const latest = this.persistedThreads()[0];
    const thread = active ?? latest ?? this.createThread();
    this.activeThreadId.set(thread.id);
    return thread;
  }

  createThread(): ChatThread {
    const now = new Date().toISOString();
    const thread: ChatThread = {
      id: this.createId(),
      title: 'New chat',
      messages: [],
      createdAt: now,
      updatedAt: now,
    };
    this.persistedThreads.update((threads) => [thread, ...threads]);
    this.activeThreadId.set(thread.id);
    this.persist(thread);
    return thread;
  }

  setMessages(threadId: string, messages: ChatMessage[]): void {
    this.updateThread(threadId, (thread) => ({ ...thread, messages }));
  }

  addMessage(threadId: string, message: ChatMessage): void {
    this.updateThread(threadId, (thread) => ({ ...thread, messages: [...thread.messages, message] }));
  }

  /** Updates the in-progress assistant message without persisting every streamed token. */
  updateLastMessage(threadId: string, message: ChatMessage, persist = true): void {
    const current = this.findThread(threadId);
    const lastMessage = current?.messages.at(-1);
    if (!current || !lastMessage || lastMessage.author !== 'bot') return;

    const updated = this.withMessageIds({ ...current, messages: [...current.messages.slice(0, -1), { ...message, id: lastMessage.id }], updatedAt: new Date().toISOString() });
    this.persistedThreads.set([updated, ...this.persistedThreads().filter((thread) => thread.id !== threadId)]);
    if (persist) this.persist(updated);
  }

  updateTitle(threadId: string, title: string, titledKey?: string): void {
    const cleanTitle = title.trim();
    if (!cleanTitle) return;
    this.updateThread(threadId, (thread) => ({
      ...thread,
      title: cleanTitle,
      ...(titledKey !== undefined ? { titledKey } : null),
    }));
  }

  /** Stores generated topics with the conversation so reopening it does not regenerate them. */
  setRelatedTopics(threadId: string, topics: string[]): void {
    this.updateThread(threadId, (thread) => ({ ...thread, relatedTopics: [...topics] }));
  }

  /** A new user turn makes the previous turn's suggested follow-ups stale. */
  clearRelatedTopics(threadId: string): void {
    this.updateThread(threadId, ({ relatedTopics: _relatedTopics, ...thread }) => thread);
  }

  /** Permanently removes a conversation thread from storage. */
  deleteThread(threadId: string): void {
    this.persistedThreads.update((threads) => threads.filter((thread) => thread.id !== threadId));
    if (this.activeThreadId() === threadId) {
      this.activeThreadId.set(null);
    }
    this.threadSync?.delete(threadId);
    this.persist();
  }
  
  // Settings functionality
  saveSettings(settings: Settings): void {
    localStorage.setItem('app-settings', JSON.stringify(settings));
  }
  
  getSettings(): Settings | null {
    const settings = localStorage.getItem('app-settings');
    if (!settings) {
      return null;
    }
    try {
      const savedSettings = JSON.parse(settings) as Partial<Settings> & { rootUrl?: unknown };
      // Migrate the previous single-endpoint format without losing a user's setup.
      const { rootUrl, ...currentSettings } = savedSettings;
      const legacyRootUrl = typeof rootUrl === 'string' ? rootUrl : undefined;
      const mainApiUrl = typeof savedSettings.mainApiUrl === 'string'
        ? savedSettings.mainApiUrl
        : (legacyRootUrl === '/api' ? environment.lmStudioRootUrl : legacyRootUrl) || environment.rootUrl;
      const helper = savedSettings.helperModel;
      return {
        ...(typeof currentSettings.model === 'string' ? { model: currentSettings.model } : {}),
        ...(typeof currentSettings.temperature === 'number' ? { temperature: currentSettings.temperature } : {}),
        ...(currentSettings.reasoningEffort === 'low' || currentSettings.reasoningEffort === 'medium' || currentSettings.reasoningEffort === 'high'
          ? { reasoningEffort: currentSettings.reasoningEffort }
          : {}),
        ...(typeof currentSettings.logLlmRequests === 'boolean' ? { logLlmRequests: currentSettings.logLlmRequests } : {}),
        ...(typeof currentSettings.relatedTopicGenerationEnabled === 'boolean'
          ? { relatedTopicGenerationEnabled: currentSettings.relatedTopicGenerationEnabled }
          : {}),
        ...(helper && typeof helper === 'object'
          ? {
              helperModel: {
                enabled: (helper as Partial<HelperModelSettings>).enabled === true,
                ...(typeof (helper as Partial<HelperModelSettings>).apiUrl === 'string'
                  ? { apiUrl: (helper as Partial<HelperModelSettings>).apiUrl }
                  : {}),
                ...(typeof (helper as Partial<HelperModelSettings>).modelId === 'string'
                  ? { modelId: (helper as Partial<HelperModelSettings>).modelId }
                  : {}),
              },
            }
          : {}),
        mainApiUrl,
        // `auto` was the previous LM Studio/router-only default and is not an
        // OpenAI model identifier.
        model: this.isOpenAiUrl(mainApiUrl) && savedSettings.model === 'auto'
          ? environment.llmModel
          : typeof savedSettings.model === 'string' ? savedSettings.model : undefined,
      };
    } catch {
      // A malformed legacy value should not make the settings page unusable.
      return null;
    }
  }
  
  /**
   * Effective (merged) configuration: user-saved values take precedence,
   * falling back to the shared defaults on a per-field basis.
   */
  getEffectiveSettings(): Required<Settings> {
    const saved: Partial<Settings> = this.getSettings() ?? {};
    return {
      ...DEFAULT_SETTINGS,
      ...saved,
      helperModel: { ...DEFAULT_SETTINGS.helperModel, ...saved.helperModel },
    };
  }

  resetSettings(): Required<Settings> {
    localStorage.removeItem('app-settings');
    return { ...DEFAULT_SETTINGS };
  }

  clearChatHistory(): void {
    this.persistedThreads.set([]);
    this.activeThreadId.set(null);
    localStorage.removeItem(CHAT_THREADS_STORAGE_KEY);
    this.threadSync?.clear();
  }

  private findThread(threadId: string): ChatThread | undefined {
    return this.persistedThreads().find((thread) => thread.id === threadId);
  }

  private updateThread(threadId: string, change: (thread: ChatThread) => ChatThread): void {
    const current = this.findThread(threadId);
    if (!current) return;

    const updated = this.withMessageIds({ ...change(current), updatedAt: new Date().toISOString() });
    this.persistedThreads.set([updated, ...this.persistedThreads().filter((thread) => thread.id !== threadId)]);
    this.persist(updated);
  }

  private readThreads(): ChatThread[] {
    try {
      const value = localStorage.getItem(CHAT_THREADS_STORAGE_KEY);
      if (!value) return [];
      const threads = JSON.parse(value) as unknown;
      if (!Array.isArray(threads)) return [];

      return threads.filter(this.isThread).map((thread) => this.withMessageIds(thread)).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    } catch {
      return [];
    }
  }

  private isThread(value: unknown): value is ChatThread {
    if (!value || typeof value !== 'object') return false;
    const thread = value as Partial<ChatThread>;
    return typeof thread.id === 'string'
      && typeof thread.title === 'string'
      && Array.isArray(thread.messages)
      && typeof thread.createdAt === 'string'
      && typeof thread.updatedAt === 'string';
  }

  private persist(changedThread?: ChatThread): void {
    localStorage.setItem(CHAT_THREADS_STORAGE_KEY, JSON.stringify(this.persistedThreads()));
    if (changedThread) this.threadSync?.upsert(changedThread);
  }

  private withMessageIds(thread: ChatThread): ChatThread {
    return {
      ...thread,
      messages: thread.messages.map((message) => message.id ? message : { ...message, id: this.createId() }),
    };
  }

  private createId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private isOpenAiUrl(url?: string): boolean {
    try {
      return new URL(url ?? '').hostname === 'api.openai.com';
    } catch {
      return false;
    }
  }
}
